import { useEffect, useState } from "react";
import { Column, ContentBox, ContentPage, NoResults, ProgressBar, Row } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { canOpenScreen } from "@helpers/companyScreens";
import { companyPendingItems, declarationTypeBars } from "@helpers/dashboard";
import { formatCompetencia, formatHours, usePageHeader } from "@components/ow";
import {
  AllClear,
  DASHBOARD_HEADER,
  DashboardBlock,
  FitBox,
  PendingNotice,
  PendingSkeleton,
  Stack,
  StatCards,
  totalOf,
} from "../../Common/Dashboard";

// Dashboard da empresa (/ do ADM; modelo Dashboard do DS OneWork): os números da
// empresa, o que espera uma decisão e as declarações por tipo. Cada número vem da
// mesma consulta da tela do atalho, então bate com o que a pessoa vê ao clicar.

/** Horas aguardando na competência, do consolidado da Aprovação de horas. */
function waitingHoursOf(settled) {
  if (settled.status !== "fulfilled") return null;
  const data = settled.value?.data || settled.value || [];
  const list = Array.isArray(data) ? data : [];
  const waiting = list.filter((c) => (c.waiting_hour_ids || []).length > 0);
  const hours = waiting.reduce((sum, c) => sum + (parseFloat(c.total_hours_waiting) || 0), 0);
  return { text: formatHours(hours * 60), contractors: waiting.length };
}

export default function Dashboard() {
  const userService = useService("users");
  const quizService = useService("quiz");
  const declarationService = useService("declarations");
  const projectHours = useService("projectHours");
  const taxInvoicesService = useService("taxInvoices");
  const valueInputService = useService("contract-value-inputs");
  const timeClockService = useService("timeClock");

  const [stats, setStats] = useState(null);
  const [declarations, setDeclarations] = useState(null);
  const [pending, setPending] = useState(null);

  usePageHeader(DASHBOARD_HEADER);

  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const can = (screen) => canOpenScreen(screen, user);

  const now = new Date();
  const competence = { month: now.getMonth() + 1, year: now.getFullYear() };
  const competenceText = formatCompetencia(competence, true);

  useEffect(() => {
    fetchStats();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchStats() {
    // O total de declarações é a soma dos tipos: uma chamada só para o cartão e as barras
    const [employees, quizzes, graph] = await Promise.allSettled([
      userService.list("role=EMP&page=1&limit=1"),
      quizService.list("status=ATIVO&page=1&limit=1"),
      declarationService.getCompanyGraphData(),
    ]);
    const graphData = graph.status === "fulfilled" && !graph.value?.error ? graph.value?.data : null;
    const byType = graphData ? declarationTypeBars(graphData.types) : null;
    setDeclarations(byType || { total: 0, bars: [], failed: true });
    setStats({ employees: totalOf(employees), quizzes: totalOf(quizzes), declarations: byType ? byType.total : null });
  }

  async function fetchPending() {
    const reference = `${competence.year}-${String(competence.month).padStart(2, "0")}`;
    const [hours, pendingInvoices, approvedInvoices, valueInputs, withoutValue, adjustments] = await Promise.allSettled([
      projectHours.consolidated(reference),
      taxInvoicesService.list("scope=company&status=PENDING&page=1&limit=1"),
      taxInvoicesService.list("scope=company&status=APPROVED&page=1&limit=1"),
      valueInputService.listPending("PENDING"),
      valueInputService.getPendingCount(competence.month, competence.year),
      timeClockService.listAdjustments("status=PENDING&scope=company&page=1&limit=1"),
    ]);
    const listLength = (settled) =>
      settled.status === "fulfilled" && !settled.value?.error && Array.isArray(settled.value?.data)
        ? settled.value.data.length
        : null;

    const items = companyPendingItems(
      {
        hours: waitingHoursOf(hours),
        pendingInvoices: totalOf(pendingInvoices),
        approvedInvoices: totalOf(approvedInvoices),
        valueInputs: listLength(valueInputs),
        contractsWithoutValue: totalOf(withoutValue),
        adjustments: totalOf(adjustments),
      },
      competenceText
    );
    setPending(items.filter((item) => can(item.screen)));
  }

  const statItems = [
    can("employees") && {
      key: "employees",
      label: "Colaboradores",
      value: stats?.employees,
      link: "Ver colaboradores",
      route: "/c/employees",
    },
    { key: "quizzes", label: "Questionários ativos", value: stats?.quizzes, link: "Ver questionários", route: "/c/quiz" },
    {
      key: "declarations",
      label: "Declarações",
      value: stats?.declarations,
      link: "Ver declarações",
      route: "/c/declarations",
    },
  ].filter(Boolean);

  const hasDeclarations = declarations && declarations.total > 0;

  return (
    <ContentPage>
      <FitBox>
        <ContentBox>
          <StatCards items={statItems} loading={!stats} />
        </ContentBox>
      </FitBox>

      <ContentBox>
        <Row $gap={30} $alignItems="flex-start">
          <Column $columns={7}>
            <DashboardBlock title="Pendências">
              {!pending ? (
                <PendingSkeleton />
              ) : pending.length === 0 ? (
                <AllClear text="Nenhuma pendência da empresa agora." />
              ) : (
                <Stack>
                  {pending.map((item) => (
                    <PendingNotice key={item.key} {...item} />
                  ))}
                </Stack>
              )}
            </DashboardBlock>
          </Column>
          <Column $columns={5}>
            <DashboardBlock
              title="Declarações por tipo"
              action={hasDeclarations ? { text: "Ver declarações", route: "/c/declarations" } : null}
            >
              {!declarations ? (
                <PendingSkeleton />
              ) : hasDeclarations ? (
                <Stack $gap="16px">
                  {declarations.bars.map((bar) => (
                    <ProgressBar
                      key={bar.label}
                      $value={bar.count}
                      $max={declarations.total}
                      $label={bar.label}
                      $valueText={bar.text}
                    />
                  ))}
                </Stack>
              ) : (
                <NoResults
                  notResultText={
                    declarations.failed ? "Não foi possível carregar as declarações" : "Nenhuma declaração por aqui"
                  }
                />
              )}
            </DashboardBlock>
          </Column>
        </Row>
      </ContentBox>
    </ContentPage>
  );
}
